import { useState } from 'react'
import { Download, FileText, FileJson, FileSpreadsheet, FileX, Settings, Check } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

import { useUIStore } from '@/stores/uiStore'
import exportService from '@/services/export'
import type { ExportFormat, ExportRequest, PDFReportConfig, CSVExportConfig } from '@/types/export'

interface ExportMenuProps {
  scanId: string
  isCompleted: boolean
  selectedSeverities?: string[]
  onExportStart?: () => void
  onExportComplete?: () => void
  variant?: 'button' | 'dropdown'
  size?: 'sm' | 'default' | 'lg'
}

const formatIcons = {
  json: FileJson,
  sarif: FileX,
  pdf: FileText,
  csv: FileSpreadsheet,
}

const formatDescriptions = {
  json: 'Complete scan data in JSON format',
  sarif: 'Standard security findings format',
  pdf: 'Professional formatted report',
  csv: 'Tabular data for analysis',
}

export function ExportMenu({ 
  scanId, 
  isCompleted, 
  selectedSeverities = [],
  onExportStart,
  onExportComplete,
  variant = 'button',
  size = 'default'
}: ExportMenuProps) {
  // Note: selectedSeverities is available for future filtering implementation
  console.debug('Export menu initialized with severities:', selectedSeverities)
  const { addToast } = useUIStore()
  const [isExporting, setIsExporting] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [exportFormat, setExportFormat] = useState<ExportFormat>('json')
  
  // Export options
  const [includeFindings, setIncludeFindings] = useState(true)
  const [includeSummary, setIncludeSummary] = useState(true)
  const [includeRiskScore, setIncludeRiskScore] = useState(true)
  const [includeRecommendations, setIncludeRecommendations] = useState(true)
  const [filterSeverity, setFilterSeverity] = useState<string[]>([])
  
  // PDF configuration
  const [pdfConfig, setPdfConfig] = useState<PDFReportConfig>({
    include_executive_summary: true,
    include_risk_breakdown: true,
    include_findings_details: true,
    include_remediation_guide: true,
    include_charts: true,
    max_findings_detail: 50,
  })
  
  // CSV configuration
  const [csvConfig, setCsvConfig] = useState<CSVExportConfig>({
    flatten_findings: true,
    include_raw_data: false,
    separator: ',',
  })

  const handleQuickExport = async (format: ExportFormat) => {
    if (!isCompleted) {
      addToast({
        title: 'Export Unavailable',
        description: 'Can only export completed scans.',
        type: 'error',
      })
      return
    }

    setIsExporting(true)
    onExportStart?.()

    try {
      await exportService.quickExport(scanId, format)
      addToast({
        title: 'Export Started',
        description: `Your ${format.toUpperCase()} export is downloading.`,
        type: 'success',
      })
      onExportComplete?.()
    } catch (error) {
      console.error('Export failed:', error)
      addToast({
        title: 'Export Failed',
        description: 'Could not export scan results. Please try again.',
        type: 'error',
      })
    } finally {
      setIsExporting(false)
    }
  }

  const handleAdvancedExport = async () => {
    if (!isCompleted) {
      addToast({
        title: 'Export Unavailable',
        description: 'Can only export completed scans.',
        type: 'error',
      })
      return
    }

    setIsExporting(true)
    onExportStart?.()

    try {
      const exportRequest: ExportRequest = {
        format: exportFormat,
        include_findings: includeFindings,
        include_summary: includeSummary,
        include_risk_score: includeRiskScore,
        include_recommendations: includeRecommendations,
        filter_severity: filterSeverity.length > 0 ? filterSeverity : undefined,
      }

      await exportService.exportScanWithConfig(
        scanId,
        exportRequest,
        exportFormat === 'pdf' ? pdfConfig : undefined,
        exportFormat === 'csv' ? csvConfig : undefined
      )

      addToast({
        title: 'Export Started',
        description: `Your ${exportFormat.toUpperCase()} export is downloading.`,
        type: 'success',
      })
      onExportComplete?.()
      setShowAdvanced(false)
    } catch (error) {
      console.error('Advanced export failed:', error)
      addToast({
        title: 'Export Failed',
        description: 'Could not export scan results. Please try again.',
        type: 'error',
      })
    } finally {
      setIsExporting(false)
    }
  }

  if (variant === 'dropdown') {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size={size} 
            disabled={!isCompleted || isExporting}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-4">
            <div className="space-y-2">
              <h4 className="font-medium leading-none">Quick Export</h4>
              <p className="text-sm text-muted-foreground">
                Export with default settings
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {(['json', 'pdf', 'csv', 'sarif'] as ExportFormat[]).map((format) => {
                const Icon = formatIcons[format]
                return (
                  <Button
                    key={format}
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickExport(format)}
                    disabled={isExporting}
                    className="flex flex-col h-auto py-3"
                  >
                    <Icon className="h-4 w-4 mb-1" />
                    <span className="text-xs uppercase">{format}</span>
                  </Button>
                )
              })}
            </div>

            <Dialog open={showAdvanced} onOpenChange={setShowAdvanced}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full">
                  <Settings className="h-4 w-4 mr-2" />
                  Advanced Options
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Advanced Export Options</DialogTitle>
                  <DialogDescription>
                    Customize your export with detailed configuration options.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-6">
                  {/* Format Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Export Format</label>
                    <Select value={exportFormat} onValueChange={(value) => setExportFormat(value as ExportFormat)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(['json', 'pdf', 'csv', 'sarif'] as ExportFormat[]).map((format) => {
                          const Icon = formatIcons[format]
                          return (
                            <SelectItem key={format} value={format}>
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                <span className="uppercase">{format}</span>
                                <span className="text-xs text-muted-foreground">
                                  - {formatDescriptions[format]}
                                </span>
                              </div>
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Basic Options */}
                  <div className="space-y-4">
                    <h4 className="font-medium">Include in Export</h4>
                    <div className="space-y-3">
                      {[
                        { key: 'findings', label: 'Detailed Findings', value: includeFindings, setter: setIncludeFindings },
                        { key: 'summary', label: 'Scan Summary', value: includeSummary, setter: setIncludeSummary },
                        { key: 'risk', label: 'Risk Score', value: includeRiskScore, setter: setIncludeRiskScore },
                        { key: 'recommendations', label: 'Recommendations', value: includeRecommendations, setter: setIncludeRecommendations },
                      ].map((option) => (
                        <div key={option.key} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={option.key}
                            checked={option.value}
                            onChange={(e) => option.setter(e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          <label htmlFor={option.key} className="text-sm">
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Severity Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Filter by Severity</label>
                    <div className="flex flex-wrap gap-2">
                      {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'].map((severity) => (
                        <Badge
                          key={severity}
                          variant={filterSeverity.includes(severity) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => {
                            setFilterSeverity(prev => 
                              prev.includes(severity)
                                ? prev.filter(s => s !== severity)
                                : [...prev, severity]
                            )
                          }}
                        >
                          {filterSeverity.includes(severity) && <Check className="h-3 w-3 mr-1" />}
                          {severity}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Leave empty to include all severities
                    </p>
                  </div>

                  {/* PDF-specific options */}
                  {exportFormat === 'pdf' && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">PDF Report Options</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {[
                          { key: 'executive', label: 'Executive Summary', value: pdfConfig.include_executive_summary, 
                            setter: (v: boolean) => setPdfConfig(prev => ({ ...prev, include_executive_summary: v })) },
                          { key: 'breakdown', label: 'Risk Breakdown', value: pdfConfig.include_risk_breakdown,
                            setter: (v: boolean) => setPdfConfig(prev => ({ ...prev, include_risk_breakdown: v })) },
                          { key: 'details', label: 'Findings Details', value: pdfConfig.include_findings_details,
                            setter: (v: boolean) => setPdfConfig(prev => ({ ...prev, include_findings_details: v })) },
                          { key: 'remediation', label: 'Remediation Guide', value: pdfConfig.include_remediation_guide,
                            setter: (v: boolean) => setPdfConfig(prev => ({ ...prev, include_remediation_guide: v })) },
                          { key: 'charts', label: 'Charts and Graphs', value: pdfConfig.include_charts,
                            setter: (v: boolean) => setPdfConfig(prev => ({ ...prev, include_charts: v })) },
                        ].map((option) => (
                          <div key={option.key} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`pdf-${option.key}`}
                              checked={option.value}
                              onChange={(e) => option.setter(e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            <label htmlFor={`pdf-${option.key}`} className="text-sm">
                              {option.label}
                            </label>
                          </div>
                        ))}
                        
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Max Findings Detail</label>
                          <input
                            type="number"
                            min="1"
                            max="500"
                            value={pdfConfig.max_findings_detail}
                            onChange={(e) => setPdfConfig(prev => ({ 
                              ...prev, 
                              max_findings_detail: parseInt(e.target.value) || 50 
                            }))}
                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                          />
                          <p className="text-xs text-muted-foreground">
                            Maximum number of findings to show in detail (1-500)
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* CSV-specific options */}
                  {exportFormat === 'csv' && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm">CSV Export Options</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="csv-flatten"
                            checked={csvConfig.flatten_findings}
                            onChange={(e) => setCsvConfig(prev => ({ ...prev, flatten_findings: e.target.checked }))}
                            className="rounded border-gray-300"
                          />
                          <label htmlFor="csv-flatten" className="text-sm">
                            Flatten findings into individual rows
                          </label>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="csv-raw"
                            checked={csvConfig.include_raw_data}
                            onChange={(e) => setCsvConfig(prev => ({ ...prev, include_raw_data: e.target.checked }))}
                            className="rounded border-gray-300"
                          />
                          <label htmlFor="csv-raw" className="text-sm">
                            Include raw scanner output
                          </label>
                        </div>
                        
                        <div className="space-y-2">
                          <label className="text-sm font-medium">CSV Separator</label>
                          <Select 
                            value={csvConfig.separator} 
                            onValueChange={(value) => setCsvConfig(prev => ({ ...prev, separator: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value=",">,&nbsp;&nbsp;(comma)</SelectItem>
                              <SelectItem value=";">;&nbsp;&nbsp;(semicolon)</SelectItem>
                              <SelectItem value="	">⇥&nbsp;&nbsp;(tab)</SelectItem>
                              <SelectItem value="|">|&nbsp;&nbsp;(pipe)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setShowAdvanced(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAdvancedExport} disabled={isExporting}>
                      {isExporting ? 'Exporting...' : 'Export'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Button 
      variant="outline" 
      size={size}
      onClick={() => handleQuickExport('json')}
      disabled={!isCompleted || isExporting}
    >
      <Download className="h-4 w-4 mr-2" />
      {isExporting ? 'Exporting...' : 'Export'}
    </Button>
  )
}